import React, { useState, useEffect } from 'react';
import { Table, Tag, Space, Button, message, Tooltip, Spin, Empty, Badge } from 'antd';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../services/api';

const SopExecutionRecords = ({ targetId, targetType, title }) => {
  const [loading, setLoading] = useState(false);
  const [executions, setExecutions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0
  });

  // 获取SOP执行记录
  const fetchExecutions = async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      
      // 获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId = '0';
      try {
        if (tenantStr) {
          const tenant = JSON.parse(tenantStr);
          tenantId = tenant.id.toString();
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
      }
      
      // 构建查询参数
      const params = {
        page: page - 1, // 后端分页从0开始
        size: pageSize,
        sortBy: 'createdAt',
        sortDirection: 'desc'
      };
      
      // 根据目标类型设置不同的查询参数
      if (targetType === 'GROUP') {
        params.groupId = targetId;  // 如果后端支持groupId查询
      } else if (targetType === 'CUSTOMER') {
        params.customerId = targetId;
      }
      
      // 调用API获取数据
      const response = await api.get('/v1/sop/executions', {
        params,
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      console.log('SOP执行记录响应:', response);
      
      // 处理响应数据
      let data = response.data;
      if (!data) {
        setExecutions([]);
        setPagination({
          ...pagination,
          total: 0
        });
        return;
      }
      
      // 解析分页数据
      const records = data.records || [];
      const total = data.totalRecords || 0;
      
      // 处理每条记录，确保字段名称正确
      const processedRecords = records.map(record => {
        // 确保状态字段使用正确的名称
        const status = record.status || record.executionStatus || 'unknown';
        
        // 确保当前天数和总天数字段使用正确的名称
        const currentDay = record.currentDay !== undefined ? record.currentDay : 
                         (record.current_day !== undefined ? record.current_day : 0);
                         
        const totalDays = record.totalDays !== undefined ? record.totalDays : 
                        (record.total_days !== undefined ? record.total_days : 0);
        
        return {
          ...record,
          status: status,
          currentDay: currentDay,
          totalDays: totalDays
        };
      });
      
      console.log('处理后的SOP执行记录:', processedRecords);
      
      setExecutions(processedRecords);
      setPagination({
        ...pagination,
        current: page,
        total
      });
    } catch (error) {
      console.error('获取SOP执行记录失败:', error);
      message.error('获取SOP执行记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (targetId) {
      fetchExecutions();
    }
  }, [targetId, targetType]);

  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    fetchExecutions(pagination.current, pagination.pageSize);
  };

  // 显示执行状态标签
  const renderStatus = (status) => {
    let color = 'default';
    let text = '未知';
    
    // 规范化状态字符串，处理可能的大小写和格式差异
    const normalizedStatus = String(status).toLowerCase().trim();
    
    switch (normalizedStatus) {
      case 'in_progress':
      case 'inprogress':
      case 'in-progress':
      case 'running':
        color = 'processing';
        text = '进行中';
        break;
      case 'completed':
      case 'complete':
      case 'done':
      case 'finished':
        color = 'success';
        text = '已完成';
        break;
      case 'failed':
      case 'failure':
      case 'error':
        color = 'error';
        text = '失败';
        break;
      case 'cancelled':
      case 'canceled':
      case 'cancel':
        color = 'warning';
        text = '已取消';
        break;
      case 'pending':
      case 'wait':
      case 'waiting':
        color = 'default';
        text = '等待中';
        break;
      default:
        console.warn(`未识别的状态值: ${status}`);
        break;
    }
    
    return <Tag color={color}>{text}</Tag>;
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // 表格列定义
  const columns = [
    {
      title: '推送名称',
      dataIndex: 'templateName',
      key: 'templateName',
      render: (text, record) => (
        <span>
          {text}
          {record.status === 'in_progress' && (
            <Badge status="processing" style={{ marginLeft: '8px' }} />
          )}
        </span>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: renderStatus
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 180,
      render: (text) => formatDate(text || text)
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (text) => formatDate(text)
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title || 'SOP执行记录'}</span>
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={() => fetchExecutions()}
        >
          刷新
        </Button>
      </div>
      
      <Spin spinning={loading}>
        {executions.length > 0 ? (
          <Table
            columns={columns}
            dataSource={executions}
            rowKey="id"
            pagination={pagination}
            onChange={handleTableChange}
            size="small"
          />
        ) : (
          <Empty description="暂无SOP执行记录" />
        )}
      </Spin>
    </div>
  );
};

export default SopExecutionRecords; 